import { IconDefinition, faPlus } from '@fortawesome/pro-regular-svg-icons';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import DragAndDrop from './DragAndDrop';
import Spinner from './Spinner';
import Flex from './Flex';
import Text from './Text';
import HeaderIcon from './HeaderIcon';

type Props = {
    handleDrop: (e: any) => Promise<void> | void;
    uploadFiles: (e: React.ChangeEvent<HTMLInputElement>) => Promise<void> | void;
    cancelUpload?: () => void;
    handlePastedText?: (text: string) => void;
    getDataFromClipboardEnabled?: boolean;
    clipboardImageEnabled?: boolean;
    fileInputRef: React.RefObject<HTMLInputElement>;
    allowedFiletypes?: string;
    notValid?: boolean;
    multiple?: boolean;
    small?: boolean;
    hoverFontSize?: number;
    fontSize?: number;
    className?: string;
    icon?: IconDefinition;
};

const FileUpload: React.FC<Props> = ({
    handleDrop,
    uploadFiles,
    cancelUpload,
    handlePastedText,
    getDataFromClipboardEnabled,
    clipboardImageEnabled,
    fileInputRef,
    allowedFiletypes,
    notValid,
    multiple,
    small,
    hoverFontSize,
    fontSize = 13,
    icon,
    ...props
}) => {
    const [fileIsLoading, setFileIsLoading] = useState<boolean>(false);
    const { t } = useTranslation();

    const handleUploadClick = () => {
        fileInputRef?.current?.click();
    };

    const _handleDrop = async (e: any) => {
        setFileIsLoading(true);
        try {
            await handleDrop(e);
        } finally {
            setFileIsLoading(false);
        }
    };

    const _uploadFiles = async (e: React.ChangeEvent<HTMLInputElement>) => {
        setFileIsLoading(true);
        try {
            await uploadFiles(e);
        } finally {
            setFileIsLoading(false);
        }
    };

    return (
        <StyledRectangle
            disabled={fileIsLoading}
            onClick={() => (!fileIsLoading ? handleUploadClick() : null)}
            {...props}
            notValid={notValid}
        >
            <DragAndDrop
                hoverFontSize={hoverFontSize}
                onDrop={_handleDrop}
                onPastedText={handlePastedText}
                getDataFromClipboardEnabled={getDataFromClipboardEnabled}
                clipboardImageEnabled={clipboardImageEnabled}
                disabled={fileIsLoading}
            >
                {fileIsLoading ? (
                    <>
                        <Spinner useFullHeight={false} />
                        <Flex className="mt-1" column>
                            <Text className="f1-200" fontSize={fontSize} color="textLight">
                                {t('file.processingFileUpload')}
                            </Text>
                            <CancelButton
                                className="f1-200"
                                fontSize={fontSize}
                                onClick={() => {
                                    cancelUpload?.();
                                    setFileIsLoading(false);
                                }}
                            >
                                {t('action.cancel')}
                            </CancelButton>
                        </Flex>
                    </>
                ) : (
                    <div className="p-2">
                        <StyledHeaderIcon icon={icon ?? faPlus} />
                        <Text className="f3-400" fontSize={fontSize} color="textLight">
                            <span
                                dangerouslySetInnerHTML={{
                                    __html: t('file.dragAndDropOrBrowse', 'Drag and drop or <u>browse</u>'),
                                }}
                            />
                        </Text>
                        {allowedFiletypes && (
                            <Text className="f3-400 mt-1" fontSize={fontSize} color="textLight">
                                {t('file.allowedType', 'Allowed file types')}: {allowedFiletypes}
                            </Text>
                        )}
                    </div>
                )}
                <HiddenInput
                    multiple={multiple}
                    id={'customer-file-upload'}
                    type={'file'}
                    ref={fileInputRef}
                    onChange={_uploadFiles}
                />
            </DragAndDrop>
        </StyledRectangle>
    );
};

const StyledRectangle = styled.div<{ notValid?: boolean; disabled?: boolean }>`
    width: 100%;
    height: 100%;
    color: #003775;
    border: 1px dashed ${({ theme, notValid }) => (!notValid ? theme.colors.textLight : theme.colors.error)};
    box-sizing: border-box;
    border-radius: var(--radius);
    text-align: center;
    cursor: pointer;
    ${({ disabled }) => disabled && 'pointer-events: none;'};
`;

const StyledHeaderIcon = styled(HeaderIcon)`
    width: 16px;
`;

const HiddenInput = styled.input`
    display: none;
`;

const CancelButton = styled.a<{ fontSize?: number }>`
    pointer-events: all;
    border: none;
    padding: 0;
    font-size: ${({ fontSize }) => fontSize || 16}px;
    color: ${({ theme }) => theme.colors.text};
`;

export default FileUpload;
